import React, { useEffect } from "react";
import { AsSelectPlaceholders, Button, DateInput, FormColumn, Input, Modal } from "best-common-react";
import { useTranslation } from "react-i18next";
import { ForeignEducationSchoolDTO } from "../../../../types/ProspectProfile";
import { DATE_INPUT_MIN_DATE, dateFormats } from "../../../../util/ProfileUtils";
import { parse, format } from "date-fns";

type AddForeignEducationSchoolModalProps = {
  show: boolean;
  onSave: (school: ForeignEducationSchoolDTO) => Promise<void>;
  onClose: () => void;
};

const defaultSchool: ForeignEducationSchoolDTO = {
  foreignEducationSchoolId: undefined,
  schoolName: "",
  lastClassDate: undefined,
};

const AddForeignEducationSchoolModal: React.FC<AddForeignEducationSchoolModalProps> = ({ show, onSave, onClose }) => {
  const { t } = useTranslation(["profile", "translation"]);
  const [school, setSchool] = React.useState<ForeignEducationSchoolDTO>(defaultSchool);
  const [canSave, setCanSave] = React.useState(false);

  useEffect(() => {
    setCanSave(!!school && !!school.schoolName.length && !!school.lastClassDate);
  }, [school]);

  useEffect(() => {
    if (!show) {
      setSchool(defaultSchool);
      setCanSave(false);
    }
  }, [show]);

  const MONTHS: string[] = t("translation:months");
  const dateDropdownPlaceholders: AsSelectPlaceholders = {
    day: t("translation:day" as const),
    month: t("translation:month" as const),
    year: t("translation:year" as const),
  };

  const saveFunc = async () => {
    await onSave(school);
  };

  return (
    <Modal show={show}>
      <Modal.Header>{t("profile:foreignSchoolModalHeader")}</Modal.Header>
      <Modal.Body>
        <div className="row mb-2">
          <FormColumn>
            <Input
              id="school-name-input"
              label={t("profile:foreignSchoolName")}
              required
              value={school?.schoolName}
              onChange={(value) => setSchool({ ...school, schoolName: value })}
            />
          </FormColumn>
        </div>
        <div className="row mb-2">
          <FormColumn>
            <DateInput
              id="date-of-last-class-input"
              label={t("profile:dateOfLastClass")}
              invalidDateMessage={t("translation:invalidDate")}
              asSelects
              asSelectsPlaceholders={dateDropdownPlaceholders}
              monthsValueOptions={MONTHS}
              minDate={DATE_INPUT_MIN_DATE}
              maxDate={new Date()}
              value={
                !!school?.lastClassDate ? parse(school?.lastClassDate, dateFormats.YYYY_MM_DD, new Date()) : undefined
              }
              onChange={(value: Date | undefined) => {
                setSchool({ ...school, lastClassDate: !!value ? format(value, dateFormats.YYYY_MM_DD) : undefined });
              }}
              required
            />
          </FormColumn>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={saveFunc} disabled={!canSave} className="me-1" variant="primary">
          {t("translation:continue")}
        </Button>
        <Button onClick={onClose} variant="secondary">
          {t("translation:cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddForeignEducationSchoolModal;
